import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { add, deleteRecord, fetch, update } from '../services/ProductLineItemsService';
import { ProductLineItem } from '../types';

export const useProductLineItem = (productId: string, productLineItemId?: string) => {
    if (!productId) {
        throw new Error('productId is required parameter');
    }

    const queryClient = useQueryClient();

    // FETCH
    const productLineItemQuery = useQuery({
        queryKey: ['product-line-item', productId, productLineItemId],
        queryFn: () => fetch(productLineItemId!),
        enabled: !!productLineItemId
    });

    // CREATE MUTATION
    const createMutation = useMutation({
        mutationFn: (values: ProductLineItem) => {
            const { id: _id, created: _created, updated: _updated, product: _product, productFeature: _productFeature, createdByUsername: _createdByUsername, createdBy: _createdBy, ...rest } = values;
            return add({ ...rest, productId });
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['product-line-items', productId] });
        }
    });

    // UPDATE MUTATION
    const updateMutation = useMutation({
        mutationFn: (values: ProductLineItem) => {
            if (!productLineItemId) throw new Error('productLineItemId is required parameter');
            const { created: _created, updated: _updated, product: _product, productFeature: _productFeature, createdByUsername: _createdByUsername, createdBy: _createdBy, ...rest } = values;
            return update(productLineItemId, rest as ProductLineItem);
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['product-line-items', productId] });
            queryClient.invalidateQueries({ queryKey: ['product-line-item', productId, productLineItemId] });
        }
    });

    // DELETE MUTATION
    const deleteMutation = useMutation({
        mutationFn: () => {
            if (!productLineItemId) throw new Error('productLineItemId is required parameter');
            return deleteRecord(productLineItemId);
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['product-line-items', productId] });
        }
    });

    return {
        record: productLineItemQuery.data,
        loading: productLineItemQuery.isLoading,
        fetchRecord: productLineItemQuery.refetch,
        createRecord: createMutation.mutate,
        updateRecord: updateMutation.mutate,
        deleteRecord: deleteMutation.mutate,
        createMutation,
        updateMutation,
        deleteMutation
    };
};